// "New screen" as a page rather than a dialog.
//
// A screen is the one entity here that cannot be created in isolation: it is
// keyed to a stream, so the location has to be chosen before Save has anything
// valid to send. That is the extra field, and the only thing that makes this
// page differ from the others.
//
// A subset of the screen detail page, necessarily: the display URL and the
// estimate history describe a screen that exists. Tuning is a property of the
// screen and could live here, but it is left to the detail page — the defaults
// are the right starting point for every screen, and asking about outlier
// trimming before the thing exists puts the hardest decision first.

import { Button, Card, Input, Select, toast } from "@pej/kore/components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";
import { ApiError } from "@/api/error";
import { useLocations } from "@/api/locations";
import { useCreateScreen } from "@/api/screens";
import { toastMutationError } from "@/components/common/toast-mutation-error";
import { applyProblemDetails, Field, Form, useKoreForm } from "@/lib/forms";

export const Route = createFileRoute("/w/$workspaceId/screens/new")({
    component: NewScreenPage,
});

const newScreenSchema = z.object({
    locationId: z.string().min(1, "Pick a location"),
    name: z.string().trim().min(1, "Name is required"),
});

type NewScreenValues = z.infer<typeof newScreenSchema>;

function NewScreenPage() {
    const { workspaceId } = Route.useParams();
    const navigate = useNavigate();
    const locationsQuery = useLocations(workspaceId);
    const locations = locationsQuery.data ?? [];
    // With one location there is nothing to choose, so choose it.
    const onlyLocation = locations.length === 1 ? locations[0] : undefined;

    const form = useKoreForm({
        schema: newScreenSchema,
        defaultValues: { locationId: "", name: "" },
    });
    const locationId = form.watch("locationId");

    // The single-location default is only knowable once the query resolves,
    // which happens after the form mounts — hence an effect rather than a
    // default value. Guarded on the field being untouched so it never
    // overwrites a choice someone has already made.
    useEffect(() => {
        if (onlyLocation && !form.getValues("locationId")) {
            form.setValue("locationId", onlyLocation.id);
        }
    }, [onlyLocation, form]);
    const createScreen = useCreateScreen(locationId);

    async function onSubmit(values: NewScreenValues) {
        const location = locations.find((s) => s.id === values.locationId);
        if (!location) return;
        try {
            const created = await createScreen.mutateAsync({
                name: values.name,
                streamId: location.stream.streamId,
            });
            toast.success("Screen created");
            navigate({
                to: "/w/$workspaceId/screens/$screenId",
                params: { workspaceId, screenId: created.id },
            });
        } catch (err) {
            if (err instanceof ApiError) {
                // Violations sit on their fields; anything else is a toast.
                const { onFields, unmapped } = applyProblemDetails(form, err);
                if (!onFields) toastMutationError("Create failed", err, { unmapped });
                return;
            }
            throw err;
        }
    }

    return (
        <div className="flex flex-col gap-6">
            <h1 className="text-xl font-semibold tracking-tight">New screen</h1>
            <Card className="p-6">
                <h2 className="mb-1 text-base font-semibold">Screen details</h2>
                <p className="mb-4 text-sm text-muted-foreground">
                    Filters, copy, the display URL and the estimate tuning all live on the screen's own page.
                </p>
                <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-4">
                    <Field
                        name="locationId"
                        control={form.control}
                        label="Location"
                        required
                        description="The location whose postings this screen shows."
                        render={({ field: { value, onChange, name } }) => (
                            <Select value={value} onValueChange={onChange} name={name}>
                                <Select.Trigger>
                                    <Select.Value>
                                        {(v: unknown) => locations.find((s) => s.id === v)?.name ?? "Pick a location"}
                                    </Select.Value>
                                </Select.Trigger>
                                <Select.Content>
                                    {locations.map((location) => (
                                        <Select.Item key={location.id} value={location.id}>
                                            {location.name}
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select>
                        )}
                    />
                    <Field
                        name="name"
                        control={form.control}
                        label="Name"
                        required
                        render={({ field }) => <Input {...field} placeholder="Counter screen" />}
                    />
                    <div className="flex justify-end gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate({ to: "/w/$workspaceId/screens", params: { workspaceId } })}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={createScreen.isPending}>
                            {createScreen.isPending ? "Creating…" : "Create screen"}
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
}
